import { Label, Select } from '@kmgestao/ui';

import type { FormField } from '../../schemas.js';

export interface SelectFieldProps {
  field: FormField;
  value: string;
  onChange: (next: string) => void;
  onBlur?: (() => void) | undefined;
  error?: string | undefined;
  disabled?: boolean | undefined;
  idPrefix?: string | undefined;
}

/**
 * Dropdown de opção única. Usa o `Select` do DS (native `<select>`
 * estilizado) — bom em mobile (picker nativo do iOS/Android).
 *
 * Opção vazia inicial "— Selecionar —" só aparece quando `required`
 * é true e ainda sem valor — deixa o placeholder claro. Se o campo é
 * opcional, o first-option pode ser "Nenhum" (value vazio) e isso é
 * tratado naturalmente via `field.options`.
 */
export function SelectField({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  idPrefix = 'form',
}: SelectFieldProps) {
  const inputId = `${idPrefix}-${field.id}`;
  const descId = field.description ? `${inputId}-desc` : undefined;
  const errorId = error ? `${inputId}-err` : undefined;
  const options = field.options ?? [];

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>
        {field.label}
        {field.required ? <span className="ml-0.5 text-destructive"> *</span> : null}
      </Label>
      {field.description ? (
        <p id={descId} className="text-xs text-muted-foreground">
          {field.description}
        </p>
      ) : null}
      <Select
        id={inputId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        aria-describedby={[descId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={!!error}
      >
        <option value="">— Selecionar —</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>
      {error ? (
        <p id={errorId} className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
